// Social layer (§10.4 Lobby/Podium, §10.5 honk rates, §10.6 fun rules): honks at the
// personality's rate (Gary's goose honk most of all), honk-backs within TUNE.bot.tactics
// .honkBackTicks with honkBackChance, emotes after KOs, the rare victory dance near an edge, and
// grudges against whoever KO'd the bot (§10.2). Event handling runs in `BotManager.observe`; the
// per-tick part writes the honk and emote counters through the brain.
import { NO_SLOT, TUNE } from '../constants';
import { EV, type SimEvent } from '../events';
import { BS, ModeId } from '../ids';
import { chance, rand, randIntRange } from '../rng';
import type { World } from '../types';
import type { Brain } from './brain';
import { BOT_TUNE } from './tuning';

const S = BOT_TUNE.social;
const T = TUNE.bot.tactics;
const FUN = TUNE.bot.fun;

/** Emote ids are 1..4 (§17.3 InputFrame.emote). */
const EMOTES = 4;

function scheduleEmote(br: Brain, tick: number): void {
  if (br.emoteAt >= tick) return;
  br.emoteAt = tick + randIntRange(br, S.emoteDelayMin, S.emoteDelayMax);
  br.emoteId = 1 + randIntRange(br, 0, EMOTES - 1);
}

/**
 * One sim event as seen by bot `br` (in slot `slot`). Grudges (§10.2), honk-backs (§10.4, §10.6),
 * emotes and the victory-dance roll after a KO (§10.6), retaliation memory after a hit.
 */
export function socialEvent(w: World, br: Brain, slot: number, ev: SimEvent, round: number, humans: readonly boolean[]): void {
  switch (ev.type) {
    case EV.KO: {
      if (ev.a === slot && ev.b !== NO_SLOT && ev.b !== slot) {
        br.grudge = ev.b;
        br.grudgeRound = round;
        br.grudgeUntil = 1e9;            // the rest of this round; trimmed to 30 s at the next reset
      }
      if (ev.b === slot && ev.a !== slot) {
        if (chance(br, FUN.emoteAfterKoChance)) scheduleEmote(br, w.tick);
        const scoring = w.cfg.modeId !== ModeId.LOBBY && w.cfg.modeId !== ModeId.PODIUM;
        if (scoring && !br.danceRolled) {
          br.danceRolled = true;
          if (chance(br, FUN.victoryDanceChance)) br.danceUntil = w.tick + S.danceTicks;
        }
      }
      return;
    }
    case EV.CROWN_POP:
      // The one who just lost it lunges for it at once; everyone else needs a beat to see it fly.
      br.crownPoppedBy = ev.a;
      if (ev.a === slot) br.crownLostTick = w.tick;
      else br.crownHesitateUntil = w.tick + randIntRange(br, BOT_TUNE.modes.crownPopReactMin, BOT_TUNE.modes.crownPopReactMax);
      return;
    case EV.CROWN_PICK:
      // Someone else just put the crown on: a moment to re-orient before going after them (the
      // one it was just taken from is already on it).
      // Right next to the new wearer there is nothing to re-orient: the scramble goes on.
      br.crownPickTick = w.tick;
      if (ev.a !== slot && w.tick - br.crownLostTick > BOT_TUNE.modes.crownRevengeTicks) {
        const me = w.beans[slot];
        const near = Math.hypot(me.pos.x - ev.x, me.pos.y - ev.y) < (BOT_TUNE.modes.crownMobRange[w.cfg.mapId] ?? 3.5);
        br.crownHesitateUntil = near ? -1 : w.tick + randIntRange(br, BOT_TUNE.modes.crownRetargetMin, BOT_TUNE.modes.crownRetargetMax);
      }
      return;
    case EV.HIT:
      if (ev.b === slot && ev.a !== NO_SLOT && ev.a !== slot) {
        br.lastHitBy = ev.a;
        br.lastHitByTick = w.tick;
      }
      return;
    case EV.HONK: {
      if (ev.a === slot || ev.a === NO_SLOT) return;
      if (w.tick < br.honkQuietUntil || br.honkAt >= w.tick) return;
      const honker = w.beans[ev.a];
      const me = w.beans[slot];
      // Humans get answered from anywhere. Other bots only from nearby, and only in the Lobby and
      // the Podium (§10.4): in a round, bot-to-bot replies chained into honk storms that tripled
      // every personality's honk rate (INT-A: 8 honks per bot-minute instead of §10.5's 1–8).
      const social = w.cfg.modeId === ModeId.LOBBY || w.cfg.modeId === ModeId.PODIUM;
      const near = Math.hypot(honker.pos.x - me.pos.x, honker.pos.y - me.pos.y) < S.honkBackRange;
      if (!humans[ev.a] && (!near || !social)) return;
      if (!chance(br, T.honkBackChance * (humans[ev.a] ? 1 : 0.5))) return;
      br.honkAt = w.tick + randIntRange(br, S.honkBackMinTicks, T.honkBackTicks);
      br.replyHonk = true;
      return;
    }
    default:
      return;
  }
}

/**
 * Per-tick social output: spontaneous honks at the personality's rate (more while teetering or
 * hanging: panic honks), pending honk-backs and emotes. Returns press flags as a bitmask
 * (1 honk, 2 emote); the manager turns them into counters.
 */
export function socialTick(w: World, br: Brain, state: BS): number {
  let out = 0;
  if (br.honkAt >= 0 && w.tick >= br.honkAt) {
    br.honkAt = -1;
    br.lastHonk = w.tick;
    if (br.replyHonk) br.honkQuietUntil = w.tick + S.honkReplyCooldownTicks;
    br.replyHonk = false;
    out |= 1;
  } else if (br.honkAt < 0 && state !== BS.FROZEN) {
    const panic = state === BS.TEETER || state === BS.LEDGE || state === BS.FALLING ? 4 : 1;
    const perTick = (br.p.honksPerMin * panic) / 3600;
    if (rand(br) < perTick) {
      br.lastHonk = w.tick;
      br.replyHonk = false;
      out |= 1;
    }
  }
  if (br.emoteAt >= 0 && w.tick >= br.emoteAt) {
    br.emoteAt = -1;
    br.emote = br.emoteId;
    out |= 2;
  }
  return out;
}

/** Start-of-round grudge bookkeeping: a grudge lasts 30 s into the round after it was earned. */
export function carryGrudge(br: Brain, round: number): void {
  if (br.grudge === NO_SLOT) return;
  if (br.grudgeRound === round - 1) br.grudgeUntil = TUNE.bot.attack.grudgeTicks;
  else if (br.grudgeRound !== round) {
    br.grudge = NO_SLOT;
    br.grudgeUntil = -1e9;
  }
}
